<script setup>
import { ref, computed, onMounted } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { useNavigator } from '@/composables/navigator'
import { useChips } from '@/composables/chips'
import { useCurrency } from '@/composables/currency'
import { useArchive } from '@/composables/archive'
import { useNotify } from '@/composables/notify'
import { useDetectDevice } from '@/composables/device'
import { useHighlightNewRow } from '@/composables/scroll'

import NavigateBack from '@/components/shared/NavigateBack.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ListingEdit from '@/components/shared/ListingEdit.vue'
import ListingItemEdit from '@/components/shared/ListingItemEdit.vue'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
})

const i18n = getI18n()
const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { getTagChips, getListingChips } = useChips()
const { getCurrencyString } = useCurrency()
const { notifyWarning } = useNotify()
const { isMobile } = useDetectDevice()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()

const { GROUP } = portalStore.serviceTypeEnum

const getCurrencyCode = (currencyRefId) =>
  webSiteStore.currencies.find(c => c.id === currencyRefId)?.code

const unitSymbolById = computed(() =>
  Object.fromEntries(webSiteStore.getLocalizedUnitsOfMeasure().map(u => [u.id, u.symbol])))
const vatLabelById = computed(() =>
  Object.fromEntries(webSiteStore.getLocalizedVatTypes().map(v => [v.id, v.name])))

const serviceName = (item) => planningStore.getServiceName(planningStore.getServiceById(item.service))
const serviceAvatar = (item) => item.service_type === GROUP ? null : (item.service_avatar_file || item.service_avatar_url)
const serviceAvatarIcon = (item) => item.service_type === GROUP ? 'mdi-select-group' : undefined
const unitSymbol = (item) => unitSymbolById.value[item.unit_of_measure_ref]
const vatLabel = (item) => vatLabelById.value[item.vat_type_ref]
const priceLabel = (item) => item.price != null ? getCurrencyString(item.price, getCurrencyCode(item.currency_ref)) : '—'
const costLabel = (item) => item.cost != null ? getCurrencyString(item.cost, getCurrencyCode(item.currency_ref)) : '—'

const listing = ref({ ...props.modelValue })
const listingItems = ref([])
const rowsContainer = ref(null)
const editItem = ref(null)
const editListingItem = ref(null)
let changed = false

const emit = defineEmits(['cancel', 'update'])

const { archiveAsync } = useArchive({
  onArchive: async (id) => { 
  await planningStore.archiveListingAsync(id)
   },
})

const onArchiveClick = async () => {
  await archiveAsync(listing.value.id, listing.value.archived)
  listing.value = { ...listing.value, archived: !listing.value.archived }
  changed = true
}

const onNavigateBack = () => {
  changed ? emit('update') : emit('cancel')
}
useNavigator(onNavigateBack)

const loadListingItemsAsync = async () => {
  listingItems.value = (await planningStore.fetchListingItemsAsync({ listing: listing.value.id, all: true })) || []
}

onMounted(async () => {
  const serviceFetches = []
  if (planningStore.equipments.length === 0) serviceFetches.push(planningStore.fetchEquipmentsAsync({ all: true }, true))
  if (planningStore.staff.length === 0) serviceFetches.push(planningStore.fetchStaffsAsync({ all: true }, true))
  if (planningStore.groups.length === 0) serviceFetches.push(planningStore.fetchGroupsAsync({ all: true }, true))
  if (serviceFetches.length > 0) await Promise.all(serviceFetches)
  await loadListingItemsAsync()
})

const onListingEditUpdate = async () => {
  listing.value = (await planningStore.fetchListingsAsync({ id: listing.value.id }))?.[0] || listing.value
  editItem.value = null
  changed = true
}

const onListingItemChanged = async () => {
  const wasCreated = !editListingItem.value?.id
  const previousIds = new Set(listingItems.value.map(i => i.id))
  await loadListingItemsAsync()
  editListingItem.value = null
  changed = true
  if (wasCreated) {
    const newItem = listingItems.value.find(i => !previousIds.has(i.id))
    await highlightRowAsync(newItem?.id, rowsContainer)
  }
}

const onListingItemClick = (item) => {
  if (listing.value.archived) {
    notifyWarning(i18n.t('warning.archivedListing'))
    return
  }

  editListingItem.value = item
}

</script>
<template>
  <q-card class="dialog-card-flex">
    <q-dialog
      :model-value="editItem !== null"
      :maximized="isMobile"
      persistent
      class="dialog-sm"
    >
      <listing-edit
        v-if="editItem !== null"
        :model-value="editItem"
        @update="onListingEditUpdate"
        @cancel="editItem = null"
        @delete="emit('update')"
      />
    </q-dialog>
    <q-dialog
      :model-value="editListingItem !== null"
      :maximized="isMobile"
      persistent
      class="dialog-sm"
    >
      <listing-item-edit
        v-if="editListingItem !== null"
        :model-value="editListingItem"
        :listing-id="listing.id"
        @update="onListingItemChanged"
        @cancel="editListingItem = null"
        @delete="onListingItemChanged"
      />
    </q-dialog>
    <navigate-back
      :text="listing.name"
      :archived="listing.archived"
      @cancel="onNavigateBack"
    >
      <template #right>
        <div style="display: flex; align-items: center;">
          <q-btn
            v-show="!listing.archived"
            flat
            round
            icon="mdi-pencil"
            @click="editItem = listing"
          />
          <q-btn
            flat
            round
            :icon="listing.archived ? 'mdi-archive-arrow-up' : 'mdi-archive'"
            @click="onArchiveClick"
          />
        </div>
      </template>
    </navigate-back>
    <q-card-section class="scroll col q-pa-none dialog-card-content-scroll">
      <q-card-section class="q-py-sm q-px-sm">
        <q-list>
          <q-item>
            <q-item-section>
              <q-item-label caption>
                {{ $t('common.company') }}
              </q-item-label>
              <q-item-label class="align-items-baseline">
                <chip-template
                  :values="getListingChips(listing)"
                  :style="{ 'margin-left': '0rem' }"
                />
              </q-item-label>
            </q-item-section>
            <q-item-section>
              <q-item-label caption>
                {{ $t('common.listing') }}
              </q-item-label>
              <q-item-label class="align-items-baseline">
                {{ listing.description || '-' }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>
      <q-separator />
      <div v-if="!listing.archived" class="rows-action-bar">
        <div style="flex: 1;" />
        <q-btn
          style="padding: 0;"
          size="md"
          unelevated
          ripple
          rounded
          icon="mdi-plus-circle-outline"
          @click="editListingItem = {}"
        />
      </div>
      <q-card-section class="q-pa-sm rows-section">
        <div class="rows-scroll-area">
          <div ref="rowsContainer" class="rows-scroll-inner">
            <q-list dense>
              <q-item
                v-for="item in listingItems"
                :key="item.id"
                v-ripple
                class="q-pa-none"
                :class="{ 'row-highlight': item.id === highlightedRowId }"
                clickable
                @click="onListingItemClick(item)"
              >
                <list-item
                  :avatar="serviceAvatar(item)"
                  :avatar-icon="serviceAvatarIcon(item)"
                  :label="item.name"
                  :caption-chips="getTagChips(item.tags)"
                  dense
                >
                  <template #caption>
                    <span v-if="serviceName(item)" class="text-weight-medium">{{ serviceName(item) }}</span>
                    <span v-if="item.description" class="text-grey-7">{{ serviceName(item) ? ' · ' : '' }}{{ item.description }}</span>
                  </template>
                  <template #right>
                    <q-item-section avatar class="q-px-none" style="align-items: flex-end;">
                      <q-item-label class="text-positive text-weight-bold">
                        {{ priceLabel(item) }}<span v-if="unitSymbol(item)" class="text-grey-6 text-weight-regular"> / {{ unitSymbol(item) }}</span>
                        <q-icon
                          name="mdi-label"
                          size="xs"
                          class="q-ml-xs"
                          style="transform: scaleX(-1);"
                        />
                      </q-item-label>
                      <q-item-label caption class="text-red-4 text-weight-medium">
                        {{ costLabel(item) }}
                      </q-item-label>
                      <q-item-label v-if="vatLabel(item)" caption class="text-grey-6">
                        {{ i18n.t('common.vat') }} {{ vatLabel(item) }}
                      </q-item-label>
                    </q-item-section>
                  </template>
                </list-item>
              </q-item>
              <q-item v-if="listingItems.length === 0">
                <q-item-section class="text-grey-6 text-center q-py-md">
                  {{ i18n.t('common.noItemsAlt') }}
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </div>
      </q-card-section>
    </q-card-section>
  </q-card>
</template>
<style scoped lang="scss">
.rows-action-bar {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: .5rem 1rem;
  padding-bottom: 0;
}

.rows-scroll-area {
  overflow: visible;
}

.rows-scroll-inner {
  direction: ltr;
}

@media (max-width: 820px) {
  .dialog-card-content-scroll {
    overflow: hidden !important;
    display: flex !important;
    flex-direction: column !important;
  }

  .rows-section {
    flex: 1 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    padding: .5rem !important;
  }

  .rows-scroll-area {
    flex: 1;
    direction: rtl;
    overflow-y: auto;
  }

  .rows-scroll-inner {
    direction: ltr;
  }
}
</style>
